import { laptopMax, laptopMaxXL, mobileMax, mobileXXLargeMax } from '@propertypal/shared/src/constants/mediaQueries';
import { MAX_WIDTH } from '@propertypal/web-ui/src/layout/PageWrapper.style';
import Link from 'next/link';
import styled from 'styled-components';

const INPUT_HEIGHT = 44;

export const filterBreakpoint = 'max-width: 940px';

export const Container = styled.form`
  background-color: ${({ theme }) => theme.background};
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 15px 0;
  border-bottom: 1px solid ${({ theme }) => theme.keyline};

  @media (max-height: 500px) {
    position: initial;
  }
`;

export const Content = styled.div`
  display: flex;
  max-width: ${MAX_WIDTH - 20}px;
  margin: 0 auto;
  padding: 0 20px;
  gap: 10px;

  @media (${laptopMax}) {
    display: flex;
    flex-wrap: wrap;
    padding: 0 8px;

    & > button {
      display: none;
    }
  }
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.keyline};
  border-radius: 6px;
  flex: 1;

  & > div {
    height: ${INPUT_HEIGHT}px;
  }

  @media (${laptopMax}) {
    flex: 0 0 100%;
    width: 100%;
  }
`;

export const DropdownWrapper = styled.div<{ $minWidth: number }>`
  position: relative;
  flex: 0 0 175px;

  & > button {
    width: 100%;
  }

  @media (${laptopMaxXL}) {
    flex: 1;
    min-width: ${({ $minWidth }) => $minWidth}px;
  }

  @media (${laptopMax}) {
    position: initial;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 5;
  top: 54px;
  padding: 15px;
  width: calc(100vw - 30px);

  @media (${laptopMax}) {
    top: 128px;
  }
`;

export const PriceDropdown = styled(Dropdown)`
  max-width: 550px;
  right: -160px;
  display: flex;
  gap: 15px;

  @media (${laptopMax}) {
    max-width: none;
    width: calc(100vw - 30px);
    left: 8px;
  }

  @media (${mobileXXLargeMax}) {
    flex-direction: column;
  }
`;

export const BedsDropdown = styled(Dropdown)`
  max-width: 552px;
  right: -120px;

  @media (${laptopMax}) {
    max-width: none;
    width: calc(100vw - 30px);
    left: 8px;
  }
`;

export const FilterPane = styled(Dropdown)`
  max-width: 720px;
  right: 185px;
  padding: 0;
  z-index: 50;
  top: 68px;

  @media (${laptopMax}) {
    position: fixed;
    max-width: none;
    right: 0;
    left: 0;
    width: 100%;
    top: 0;
    border-radius: 0;
    background-color: rgba(0, 0, 0, 0.6);
    height: 100%;
  }
`;

export const FilterPaneContent = styled.div`
  overflow-y: scroll;
  max-height: calc(100vh - 250px);
  padding: 15px;
  background: ${({ theme }) => theme.background};

  @media (${laptopMax}) {
    max-height: calc(100% - 116px);
    margin-top: 40px;
    border-radius: 6px 6px 0 0;
  }
`;

export const FilterPaneTitle = styled.div`
  display: none;

  @media (${laptopMax}) {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
  }
`;

export const FilterPaneBottom = styled.div`
  padding: 15px;
  display: flex;
  gap: 15px;
  width: 100%;
  background: white;
  border-top: 1px solid ${({ theme }) => theme.keyline};
`;

export const StylesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(135px, 1fr));
  grid-column-gap: 15px;
  grid-row-gap: 20px;
  margin-bottom: 30px;
`;

export const StyleBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 15px;

  img {
    height: 100px;
  }

  button {
    text-align: center;
    width: 100%;
  }
`;

export const EpcArrow = styled.div`
  height: 10px;
  text-align: right;
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  max-width: 365px;
  width: 100%;

  &:before {
    display: block;
    background-image: linear-gradient(to right, white, #919191);
    content: '';
    width: calc(100% - 5px);
    height: 2px;
  }

  &:after {
    width: 0;
    height: 0;
    border-top: 5px solid rgba(0, 0, 0, 0);
    border-bottom: 5px solid rgba(0, 0, 0, 0);
    border-left: 10px solid #919191;
    display: block;
    position: absolute;
    right: 0;
    content: '';
  }
`;

export const TitleRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  align-items: center;
`;

export const BroadbandTooltip = styled.div`
  position: absolute;
  background-color: ${({ theme }) => theme.background};
  border-radius: 8px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);
  width: 300px;
  padding: 15px;
  text-align: left;

  h4 {
    color: ${({ theme }) => theme.primary};
    margin-top: 10px;
    font-size: 16px;
  }

  h4:first-child {
    margin-top: 0;
  }

  p {
    color: ${({ theme }) => theme.textDark};
    font-size: 16px;
  }

  @media (${mobileXXLargeMax}) {
    left: -170px;
  }

  @media (${mobileMax}) {
    width: 280px;
    left: -215px;
  }
`;

export const CloseToEditText = styled.div`
  text-align: right;
  margin-right: 16px;
  text-decoration: underline;
  font-weight: bold;
  transition: color 150ms linear;
  color: ${({ theme }) => theme.textDark};

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

export const CloseToInput = styled.button`
  color: inherit;
  font-size: inherit;
  display: flex;
  flex: 1 1 0%;
  min-width: 0px;
  align-items: center;
  height: ${INPUT_HEIGHT}px;
  width: 100%;

  > :not(:last-child) {
    margin-right: 8px;
  }

  &:hover {
    ${CloseToEditText} {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

export const CloseToBadge = styled.div`
  padding: 0 8px;
  border-right: 1px solid ${({ theme }) => theme.keyline};
  background-color: #b191ff;
  color: ${({ theme }) => theme.white};
  flex-basis: 110px;
  flex-shrink: 0;
  font-weight: bold;
  display: flex;
  align-items: center;
  height: 100%;

  > :not(:last-child) {
    margin-right: 8px;
  }

  svg path {
    fill: ${({ theme }) => theme.white};
  }
`;

export const CloseToSearchText = styled.input`
  flex: 1 1 0%;
  overflow: hidden;
  color: ${({ theme }) => theme.textDark};
  line-height: 22px;
  min-width: 0px;
  text-align: left;
  font: inherit;
  background: inherit;
  border: none;
  pointer-events: none;
`;

export const CurrencyColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 160px;

  @media (${mobileXXLargeMax}) {
    flex-direction: row;
    flex: 1;
    gap: 30px;
    align-items: flex-end;
  }
`;

export const PartnershipBar = styled(Link)`
  text-align: center;
  color: ${({ theme }) => theme.textDark};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  img {
    height: 30px;
  }
`;
